import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * AdminToast — the redesign's single feedback system for admin screens.
 * Renders the design's toast pill: fixed bottom-centre, teal success,
 * dark info, red error; auto-dismisses after ~2.2s.
 *
 * AdminShell already wraps every screen in <AdminToastProvider>, so screens
 * only ever need the hook:
 *
 *   const toast = useAdminToast();
 *   toast.success("Order updated");
 *   toast.info("⤓ Preparing database backup…");
 *   toast.error("Failed to save settings");
 *
 * Each call replaces the currently visible toast (matching the prototype
 * behaviour). An optional second argument overrides the dismiss delay (ms).
 */

const AdminToastContext = createContext(null);

const DEFAULT_DURATION = 2200;

export function AdminToastProvider({ children }) {
  const [toast, setToast] = useState(null); // { message, variant }
  const timerRef = useRef(null);

  const show = useCallback((message, variant = "success", duration) => {
    clearTimeout(timerRef.current);
    setToast({ message, variant });
    timerRef.current = setTimeout(
      () => setToast(null),
      typeof duration === "number" ? duration : DEFAULT_DURATION
    );
  }, []);

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    setToast(null);
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const api = useMemo(
    () => ({
      show,
      dismiss,
      success: (message, duration) => show(message, "success", duration),
      info: (message, duration) => show(message, "info", duration),
      error: (message, duration) => show(message, "error", duration),
    }),
    [show, dismiss]
  );

  return (
    <AdminToastContext.Provider value={api}>
      {children}
      {toast && (
        /* .uma wrapper so the pill is styled even though the provider is
           mounted globally (App.js), outside any admin screen's .uma root */
        <div className="uma">
          <div
            className={`adm-toast adm-toast--${toast.variant}`}
            role="status"
            aria-live="polite"
          >
            {toast.message}
          </div>
        </div>
      )}
    </AdminToastContext.Provider>
  );
}

export function useAdminToast() {
  const ctx = useContext(AdminToastContext);
  if (!ctx) {
    throw new Error(
      "useAdminToast must be used inside <AdminToastProvider> — AdminShell provides it."
    );
  }
  return ctx;
}

export default AdminToastProvider;
